export type AnyRecord = Record<string, any>;

export const EMPTY_DISPLAY = '—';
export const SAAS_DEPLOYMENT_ID = 'saas' as const;

export type SaasDeploymentId = typeof SAAS_DEPLOYMENT_ID;
export type DeploymentId = number | SaasDeploymentId | null;

export type ProductDeploymentSelection = {
  row_id: string;
  assignment_id?: number | null;
  deployment_id: DeploymentId;
  version_id: number | null;
  license_id: number | null;
};

export type ProductRelationshipSelection = {
  deployments: ProductDeploymentSelection[];
};

export const APPLIANCE_OFFER_TYPES = new Set([
  'appliance_product',
  'hybrid_product',
  'hybriud_product',
]);

export const HYBRID_OFFER_TYPES = new Set([
  'hybrid_product',
  'hybriud_product',
]);

export const SAAS_OFFER_TYPES = new Set([
  'saas_product',
]);

export const SERVICE_OFFER_TYPES = new Set([
  'services',
  'service',
]);

export const normalizeOfferType = (value: unknown): string => {
  return String(value ?? '').trim().toLowerCase();
};

export const isObject = (value: unknown): value is AnyRecord => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const makeRelationshipRowId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `relationship-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const isSaasDeployment = (value: unknown): value is SaasDeploymentId => {
  return value === SAAS_DEPLOYMENT_ID;
};

export const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    if (value === SAAS_DEPLOYMENT_ID) return null;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (isObject(value)) {
    return toNumberOrNull(value.id);
  }

  return null;
};

export const normalizeDeploymentId = (value: unknown): DeploymentId => {
  if (value === SAAS_DEPLOYMENT_ID) {
    return SAAS_DEPLOYMENT_ID;
  }

  return toNumberOrNull(value);
};

export const displayValue = (value: unknown, fallback = EMPTY_DISPLAY): string => {
  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return fallback;
};

export const uniqueNumbers = (values: unknown[]): number[] => {
  const out: number[] = [];
  const seen = new Set<number>();

  values.forEach((value) => {
    const n = toNumberOrNull(value);
    if (n == null || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  });

  return out;
};

export const getProductIdFromRelationship = (row: any): number | null => {
  return (
    toNumberOrNull(row?.product_id) ||
    toNumberOrNull(row?.product) ||
    toNumberOrNull(row?.related_product) ||
    null
  );
};

export const getVersionIdFromRelationship = (row: any): number | null => {
  return (
    toNumberOrNull(row?.version_id) ||
    toNumberOrNull(row?.version) ||
    toNumberOrNull(row?.product_version_id) ||
    toNumberOrNull(row?.product_version) ||
    toNumberOrNull(row?.related_version) ||
    null
  );
};

export const getLicenseIdFromRelationship = (row: any): number | null => {
  return (
    toNumberOrNull(row?.product_license_id) ||
    toNumberOrNull(row?.product_license) ||
    toNumberOrNull(row?.license_id) ||
    toNumberOrNull(row?.license) ||
    toNumberOrNull(row?.related_license) ||
    null
  );
};

export const getApplianceIdFromRelationship = (row: any): number | null => {
  const firstAppliance =
    Array.isArray(row?.appliances) && row.appliances.length
      ? row.appliances[0]?.appliance ?? row.appliances[0]?.appliance_detail ?? row.appliances[0]
      : null;

  return (
    toNumberOrNull(row?.appliance_id) ||
    toNumberOrNull(row?.appliance) ||
    toNumberOrNull(firstAppliance) ||
    toNumberOrNull(row?.related_appliance) ||
    null
  );
};

export const getBaseProductFromRelationship = (
  row: any,
  productCatalog: any[] = []
): any | null => {
  if (!row) return null;

  if (isObject(row.product)) {
    return row.product;
  }

  if (isObject(row.product_id)) {
    return row.product_id;
  }

  if (isObject(row.related_product)) {
    return row.related_product;
  }

  const productId = getProductIdFromRelationship(row);

  if (productId != null) {
    const found = productCatalog.find((product) => Number(product?.id) === Number(productId));
    if (found) return found;
  }

  if (
    row.name ||
    row.vendor ||
    row.sf_technology ||
    row.feature ||
    row.use_case ||
    row.component ||
    row.version ||
    row.appliance
  ) {
    return row;
  }

  return null;
};

export const getBaseProductId = (row: any, productCatalog: any[] = []): number | null => {
  const baseProduct = getBaseProductFromRelationship(row, productCatalog);

  return (
    toNumberOrNull(baseProduct?.id) ||
    getProductIdFromRelationship(row) ||
    null
  );
};

export const getBaseProductName = (row: any, productCatalog: any[] = []): string => {
  const baseProduct = getBaseProductFromRelationship(row, productCatalog);

  return displayValue(
    baseProduct?.name ||
    row?.product_name ||
    row?.name,
  );
};

export const getProductOfferType = (row: any, productCatalog: any[] = []): string => {
  const baseProduct = getBaseProductFromRelationship(row, productCatalog);
  return normalizeOfferType(baseProduct?.offer_type || row?.offer_type || row?.product_offer_type);
};

export const isApplianceOrHybridProduct = (row: any, productCatalog: any[] = []): boolean => {
  return APPLIANCE_OFFER_TYPES.has(getProductOfferType(row, productCatalog));
};

export const isHybridProduct = (row: any, productCatalog: any[] = []): boolean => {
  return HYBRID_OFFER_TYPES.has(getProductOfferType(row, productCatalog));
};

export const isSaasProduct = (row: any, productCatalog: any[] = []): boolean => {
  return SAAS_OFFER_TYPES.has(getProductOfferType(row, productCatalog));
};

export const isServiceProduct = (row: any, productCatalog: any[] = []): boolean => {
  return SERVICE_OFFER_TYPES.has(getProductOfferType(row, productCatalog));
};

export const shouldOmitProductFromRelationshipUi = (row: any, productCatalog: any[] = []): boolean => {
  return isServiceProduct(row, productCatalog);
};

export const shouldShowVersionAndApplianceForProduct = (row: any, productCatalog: any[] = []): boolean => {
  return isApplianceOrHybridProduct(row, productCatalog);
};

export const shouldShowLicenseForProduct = (row: any, productCatalog: any[] = []): boolean => {
  return isApplianceOrHybridProduct(row, productCatalog) || isSaasProduct(row, productCatalog);
};

export const shouldShowDeploymentSelectorForProduct = (
  row: any,
  productCatalog: any[] = []
): boolean => {
  return isApplianceOrHybridProduct(row, productCatalog);
};

export const shouldShowVersionForDeployment = (
  product: any,
  deploymentId: DeploymentId,
  productCatalog: any[] = []
): boolean => {
  const offerType = getProductOfferType(product, productCatalog);

  if (SAAS_OFFER_TYPES.has(offerType)) {
    return false;
  }

  if (HYBRID_OFFER_TYPES.has(offerType) && isSaasDeployment(deploymentId)) {
    return false;
  }

  return APPLIANCE_OFFER_TYPES.has(offerType);
};

export const getVersionNameFromRelationship = (row: any): string => {
  return displayValue(
    row?.version?.version ||
    row?.version_name ||
    row?.product_version?.version ||
    row?.related_version?.version,
  );
};

export const getLicenseNameFromRelationship = (row: any): string => {
  return displayValue(
    row?.product_license?.name ||
    row?.license?.name ||
    row?.license_name ||
    row?.related_license?.name,
  );
};

export const getApplianceNameFromRelationship = (row: any): string => {
  const firstAppliance =
    Array.isArray(row?.appliances) && row.appliances.length
      ? row.appliances[0]?.appliance ?? row.appliances[0]?.appliance_detail ?? row.appliances[0]
      : null;

  return displayValue(
    row?.appliance?.name ||
    firstAppliance?.name ||
    row?.appliance_name ||
    row?.related_appliance?.name,
  );
};

export const getDeploymentNameFromRelationship = (
  row: any,
  productCatalog: any[] = []
): string => {
  const offerType = getProductOfferType(row, productCatalog);
  const applianceId = getApplianceIdFromRelationship(row);
  const versionId = getVersionIdFromRelationship(row);

  if (SAAS_OFFER_TYPES.has(offerType)) {
    return 'SaaS';
  }

  if (HYBRID_OFFER_TYPES.has(offerType) && applianceId == null && versionId == null) {
    return 'SaaS';
  }

  return getApplianceNameFromRelationship(row);
};

export const getProductVersions = (product: any): any[] => {
  const versions =
    product?.version ||
    product?.versions ||
    product?.product_version ||
    [];

  return Array.isArray(versions) ? versions.filter(Boolean) : [];
};

export const getProductLicenses = (product: any): any[] => {
  const licenses =
    product?.license ||
    product?.licenses ||
    product?.product_license ||
    [];

  return Array.isArray(licenses) ? licenses.filter(Boolean) : [];
};

export const getProductAppliances = (product: any): any[] => {
  const appliances =
    product?.appliance ||
    product?.appliances ||
    [];

  return Array.isArray(appliances) ? appliances.filter(Boolean) : [];
};

export const getApplianceVersions = (appliance: any): any[] => {
  const versions =
    appliance?.version ||
    appliance?.versions ||
    appliance?.product_version ||
    appliance?.product_versions ||
    [];

  return Array.isArray(versions) ? versions.filter(Boolean) : [];
};

export const getApplianceLicenses = (appliance: any): any[] => {
  const licenses =
    appliance?.license ||
    appliance?.licenses ||
    appliance?.product_license ||
    appliance?.product_licenses ||
    [];

  return Array.isArray(licenses) ? licenses.filter(Boolean) : [];
};

export const findApplianceForDeployment = (
  product: any,
  deploymentId: DeploymentId
): any | null => {
  if (deploymentId == null || isSaasDeployment(deploymentId)) {
    return null;
  }

  return (
    getProductAppliances(product).find((appliance) => Number(appliance?.id) === Number(deploymentId)) ||
    null
  );
};

export const getVersionOptionsForDeployment = (
  product: any,
  deploymentId: DeploymentId
): any[] => {
  const appliance = findApplianceForDeployment(product, deploymentId);
  const applianceVersions = getApplianceVersions(appliance);

  return applianceVersions.length ? applianceVersions : getProductVersions(product);
};

export const getLicenseOptionsForDeployment = (
  product: any,
  deploymentId: DeploymentId
): any[] => {
  const appliance = findApplianceForDeployment(product, deploymentId);
  const applianceLicenses = getApplianceLicenses(appliance);

  return applianceLicenses.length ? applianceLicenses : getProductLicenses(product);
};

export const buildDeploymentOptions = (
  product: any,
  productCatalog: any[] = []
): Array<{
  id: DeploymentId;
  value: string;
  label: string;
  raw: any | null;
}> => {
  const offerType = getProductOfferType(product, productCatalog);
  const applianceOptions = getProductAppliances(product).map((appliance) => ({
    id: toNumberOrNull(appliance?.id),
    value: String(appliance?.id),
    label: displayValue(appliance?.name || appliance?.appliance_name || appliance?.deployment_type),
    raw: appliance,
  }));

  if (HYBRID_OFFER_TYPES.has(offerType)) {
    return [
      {
        id: SAAS_DEPLOYMENT_ID,
        value: SAAS_DEPLOYMENT_ID,
        label: 'SaaS',
        raw: null,
      },
      ...applianceOptions,
    ];
  }

  return applianceOptions;
};

export const getInitialDeploymentRowsForProduct = (
  product: any,
  productCatalog: any[] = []
): ProductDeploymentSelection[] => {
  const offerType = getProductOfferType(product, productCatalog);

  if (SERVICE_OFFER_TYPES.has(offerType)) {
    return [];
  }

  if (SAAS_OFFER_TYPES.has(offerType) || HYBRID_OFFER_TYPES.has(offerType)) {
    return [
      {
        row_id: makeRelationshipRowId(),
        assignment_id: null,
        deployment_id: SAAS_DEPLOYMENT_ID,
        version_id: null,
        license_id: getLicenseIdFromRelationship(product),
      },
    ];
  }

  if (APPLIANCE_OFFER_TYPES.has(offerType)) {
    return [
      {
        row_id: makeRelationshipRowId(),
        assignment_id: null,
        deployment_id: getApplianceIdFromRelationship(product),
        version_id: getVersionIdFromRelationship(product),
        license_id: getLicenseIdFromRelationship(product),
      },
    ];
  }

  return [
    {
      row_id: makeRelationshipRowId(),
      assignment_id: null,
      deployment_id: null,
      version_id: getVersionIdFromRelationship(product),
      license_id: getLicenseIdFromRelationship(product),
    },
  ];
};

export const getDeploymentRowsFromRelationshipRows = (
  rows: any[] = [],
  product: any = null,
  productCatalog: any[] = []
): ProductDeploymentSelection[] => {
  const baseProduct = product || rows[0];
  const offerType = getProductOfferType(baseProduct, productCatalog);

  if (!rows.length) {
    return getInitialDeploymentRowsForProduct(baseProduct, productCatalog);
  }

  return rows.map((row) => {
    const applianceId = getApplianceIdFromRelationship(row);
    const versionId = getVersionIdFromRelationship(row);
    const licenseId = getLicenseIdFromRelationship(row);

    let deploymentId: DeploymentId = applianceId;

    if (SAAS_OFFER_TYPES.has(offerType)) {
      deploymentId = SAAS_DEPLOYMENT_ID;
    }

    if (HYBRID_OFFER_TYPES.has(offerType) && applianceId == null && versionId == null) {
      deploymentId = SAAS_DEPLOYMENT_ID;
    }

    return {
      row_id: String(row?.id ?? makeRelationshipRowId()),
      assignment_id: toNumberOrNull(row?.id),
      deployment_id: deploymentId,
      version_id: versionId,
      license_id: licenseId,
    };
  });
};

export const getProductLabelWithRelationship = (
  row: any,
  productCatalog: any[] = []
): string => {
  const productName = getBaseProductName(row, productCatalog);
  const deploymentName = getDeploymentNameFromRelationship(row, productCatalog);
  const versionName = getVersionNameFromRelationship(row);
  const licenseName = getLicenseNameFromRelationship(row);

  const details = [
    deploymentName !== EMPTY_DISPLAY ? `Deployment: ${deploymentName}` : '',
    versionName !== EMPTY_DISPLAY ? `Version: ${versionName}` : '',
    licenseName !== EMPTY_DISPLAY ? `License: ${licenseName}` : '',
  ].filter(Boolean);

  if (!details.length) {
    return productName;
  }

  return `${productName} (${details.join(' • ')})`;
};

export const hydrateProductLike = (row: any, productCatalog: any[] = []): any | null => {
  const baseProduct = getBaseProductFromRelationship(row, productCatalog);
  if (!baseProduct) return null;

  const catalogProduct =
    productCatalog.find((product) => Number(product?.id) === Number(baseProduct?.id)) ||
    null;

  const merged = {
    ...(catalogProduct || {}),
    ...(baseProduct || {}),
  };

  return {
    ...merged,
    relationship: row,
    relationship_id: row?.id,
    product_id: getBaseProductId(row, productCatalog),
    version_id: getVersionIdFromRelationship(row),
    license_id: getLicenseIdFromRelationship(row),
    appliance_id: getApplianceIdFromRelationship(row),
    version_name: getVersionNameFromRelationship(row),
    license_name: getLicenseNameFromRelationship(row),
    appliance_name: getApplianceNameFromRelationship(row),
    deployment_name: getDeploymentNameFromRelationship(row, productCatalog),
  };
};

export const hydrateProductList = (rows: any[] = [], productCatalog: any[] = []): any[] => {
  return (rows || [])
    .map((row) => hydrateProductLike(row, productCatalog))
    .filter(Boolean);
};

export const buildOpportunityProductPayload = ({
                                                 product,
                                                 relationship = {},
                                                 userEmail,
                                                 opportunityID,
                                               }: {
  product: any;
  relationship?: any;
  userEmail?: string;
  opportunityID?: string | number;
}): Record<string, any> => {
  const productId = getBaseProductId(product) ?? getProductIdFromRelationship(product);

  const deploymentId = normalizeDeploymentId(
    relationship.deployment_id ??
    relationship.appliance_id ??
    relationship.appliance
  );

  const isSaasRow = isSaasDeployment(deploymentId);

  const versionId = isSaasRow
    ? null
    : (
      toNumberOrNull(relationship.version_id) ??
      toNumberOrNull(relationship.version) ??
      getVersionIdFromRelationship(product)
    );

  const licenseId =
    toNumberOrNull(relationship.license_id) ??
    toNumberOrNull(relationship.product_license_id) ??
    toNumberOrNull(relationship.product_license) ??
    toNumberOrNull(relationship.license_ids?.[0]) ??
    getLicenseIdFromRelationship(product);

  const applianceId = isSaasRow
    ? null
    : (
      toNumberOrNull(relationship.appliance_id) ??
      toNumberOrNull(relationship.appliance) ??
      toNumberOrNull(relationship.appliance_ids?.[0]) ??
      getApplianceIdFromRelationship(product)
    );

  return {
    opportunity: opportunityID,
    product: productId,
    product_id: productId,
    version: versionId,
    version_id: versionId,
    product_version: versionId,
    product_version_id: versionId,
    product_license: licenseId,
    product_license_id: licenseId,
    appliance: applianceId,
    appliance_id: applianceId,
    updated_by: userEmail || '',
  };
};

export const buildCustomerOwnedProductPayload = ({
                                                   product,
                                                   relationship = {},
                                                   userEmail,
                                                   customerId,
                                                 }: {
  product: any;
  relationship?: any;
  userEmail?: string;
  customerId?: string | number;
}): Record<string, any> => {
  const productId = getBaseProductId(product) ?? getProductIdFromRelationship(product);

  const deploymentId = normalizeDeploymentId(
    relationship.deployment_id ??
    relationship.appliance_id ??
    relationship.appliance
  );

  const isSaasRow = isSaasDeployment(deploymentId);

  const versionId = isSaasRow
    ? null
    : (
      toNumberOrNull(relationship.version_id) ??
      toNumberOrNull(relationship.version) ??
      getVersionIdFromRelationship(product)
    );

  const licenseId =
    toNumberOrNull(relationship.license_id) ??
    toNumberOrNull(relationship.product_license_id) ??
    toNumberOrNull(relationship.product_license) ??
    toNumberOrNull(relationship.license_ids?.[0]) ??
    getLicenseIdFromRelationship(product);

  const applianceId = isSaasRow
    ? null
    : (
      toNumberOrNull(relationship.appliance_id) ??
      toNumberOrNull(relationship.appliance) ??
      toNumberOrNull(relationship.appliance_ids?.[0]) ??
      getApplianceIdFromRelationship(product)
    );

  return {
    customer: customerId,
    product: productId,
    product_id: productId,
    version: versionId,
    version_id: versionId,
    license: licenseId,
    license_id: licenseId,
    product_license: licenseId,
    product_license_id: licenseId,
    appliance: applianceId,
    appliance_id: applianceId,
    updated_by: userEmail || '',
  };
};

export const expandRelationshipSelectionsToRows = ({
                                                     product,
                                                     relationship,
                                                     productCatalog = [],
                                                   }: {
  product: any;
  relationship: ProductRelationshipSelection;
  productCatalog?: any[];
}): Array<{
  deployment_id: DeploymentId;
  version_id: number | null;
  license_id: number | null;
  appliance_id: number | null;
}> => {
  const offerType = getProductOfferType(product, productCatalog);

  if (SERVICE_OFFER_TYPES.has(offerType)) {
    return [];
  }

  const deployments = relationship?.deployments?.length
    ? relationship.deployments
    : getInitialDeploymentRowsForProduct(product, productCatalog);

  return deployments.map((row) => {
    const isSaasRow =
      isSaasDeployment(row.deployment_id) ||
      SAAS_OFFER_TYPES.has(offerType);

    return {
      deployment_id: isSaasRow ? SAAS_DEPLOYMENT_ID : normalizeDeploymentId(row.deployment_id),
      version_id: isSaasRow ? null : toNumberOrNull(row.version_id),
      license_id: toNumberOrNull(row.license_id),
      appliance_id: isSaasRow ? null : toNumberOrNull(row.deployment_id),
    };
  });
};